// ============================================================================
// LA PASTILLE — géométrie et tons, décidés une fois pour les cinq surfaces.
//
// ⚠️ POURQUOI L'EXTRACTION : la pastille était réécrite dans CHAQUE app (Console `Pill` + 6 alias +
// `Pastille` + `TriageBadge`, EDI `Badge`/`Dot`, store `PlatformBadge` + 8 capsules en ligne,
// back-office `Badge` ×2 + `Pastille`). Vingt-cinq géométries dessinées pour deux spécifiées : les
// hauteurs allaient de 17 à 26 px pour le même objet, et personne ne pouvait dire laquelle était la
// bonne. Trois crans les couvrent toutes à ±2 px près — l'écart devient DÉCIDÉ, plus découvert.
//
// ⚠️ UNE PASTILLE N'EST PAS UN CONTRÔLE. Elle porte une information ; elle ne se clique pas et n'en a
// pas l'air. Le vocabulaire de filtre cliquable, c'est `.chip` — il reste à 600 de graisse, et cette
// divergence est VOULUE : ce qui s'actionne et ce qui s'énonce ne se ressemblent pas.
// ============================================================================

/**
 * Trois crans, et LA RÈGLE DE CHOIX qui va avec — sans elle, une variante devient un tirage au sort.
 * · `xs` : compteur, pastille en surimpression, pastille dans une ligne dense.
 * · `sm` : le défaut — pastille en cellule de tableau ou dans une ligne de texte.
 * · `md` : pastille d'en-tête de fiche ou de tuile, là où elle porte l'état principal de l'objet.
 */
export const BADGE_SIZES = {
  xs: { height: 19, padH: 8, fontSize: 10, icon: 9, gap: 5 },
  sm: { height: 22, padH: 9, fontSize: 11, icon: 11, gap: 6 },
  md: { height: 24, padH: 10, fontSize: 12, icon: 12, gap: 6 },
} as const;

export type BadgeSize = keyof typeof BADGE_SIZES;

/**
 * Graisse 700 : 111 sites du dépôt contre 57 à 600.
 * ⚠️ Ne pas aligner `.chip` dessus — voir l'en-tête : un contrôle ne se peint pas comme une information.
 */
export const BADGE_WEIGHT = 700;

/**
 * Le point de tête, et son halo.
 * ⚠️ 7 px et non 6 (17 sites contre 4). Le halo vient de l'EDI et reste le seul `color-mix` en oklch du
 * dépôt : il fait exister le point sur un fond clair sans lui donner une bordure, qui le ferait passer
 * pour un contrôle.
 */
export const BADGE_DOT = { size: 7, halo: 3, haloMix: 22 } as const;

/**
 * Forme `code` — mono, capitales, interlettrage ouvert.
 * ⚠️ DÉCOUPLÉE de la taille (un seul site du dépôt combinait mono et capitales) : sans ce découplage,
 * « NOUVEAU » et « RECOMMANDÉ » se liraient comme des identifiants techniques.
 */
export const BADGE_CODE = { fontSize: 10, letterSpacing: '.05em', weight: 600 } as const;

/**
 * Le compteur (cloche, onglet).
 * ⚠️ `max` = écrêtage : au-delà, « 99+ ». Un compteur à quatre chiffres déforme le déclencheur qu'il
 * annote, et la valeur exacte n'apporte rien — ce qui compte est « beaucoup ».
 */
export const BADGE_COUNT = { minWidth: 15, height: 15, padH: 4, offset: -5, max: 99 } as const;

/**
 * Les tons, en RÔLES — jamais une couleur littérale.
 * ⚠️ `ok` et non `teal` pour la sélection/succès : `ok-*` porte une surcharge de thème SOMBRE, pas
 * `teal-*`. Une pastille en `teal-100` reste peinte en clair sur un écran sombre, et c'est ce qui
 * arrivait. `teal-*` reste la rampe de MARQUE (tuiles d'icône, ligne de total).
 */
export const BADGE_TONES = {
  neutral: { bg: 'ink-100', fg: 'ink-600' },
  ok: { bg: 'ok-bg', fg: 'ok-fg' },
  warn: { bg: 'amber-bg', fg: 'amber-fg' },
  danger: { bg: 'danger-bg', fg: 'danger-ink' },
  info: { bg: 'blue-bg', fg: 'blue-fg' },
  violet: { bg: 'violet-bg', fg: 'violet-fg' },
  /** Sans fond : pour une pastille posée sur une surface déjà teintée. */
  muted: { bg: 'transparent', fg: 'ink-500' },
} as const;

export type BadgeTone = keyof typeof BADGE_TONES;

/**
 * LES COULEURS D'UNE ÉTIQUETTE LIBRE — celles qu'un exploitant peut choisir lui-même
 * (qualifications clients, et toute liste à repère visuel qui viendra).
 *
 * ⚠️ À NE PAS CONFONDRE AVEC `BADGE_TONES`. Un TON est un RÔLE que le code décide (succès, danger,
 * information) ; ces couleurs-ci sont un CHOIX DE L'EXPLOITANT, sans signification pour le
 * logiciel. Les mélanger ferait dire « danger » à une étiquette rouge que quelqu'un a choisie
 * parce qu'il aimait le rouge.
 *
 * ⚠️ PALETTE FERMÉE, et c'est le point : un sélecteur libre produit des étiquettes illisibles
 * (jaune sur blanc) et autant de nuances que d'opérateurs. Six repères se distinguent d'un coup
 * d'œil ; au-delà, la couleur cesse d'être un repère.
 *
 * ⚠️ ELLE VIT ICI, pas dans un écran : le back-office la propose, la caisse l'affiche. Deux copies
 * auraient divergé au premier ajustement de teinte.
 */
export const LABEL_COLORS = ['#16A34A', '#2563EB', '#7C5CFF', '#D97706', '#DC2626', '#6B7280'] as const;

/** Le cran d'une pastille de choix de couleur, et son espacement — figés pour que les lignes s'alignent. */
export const LABEL_COLOR_SWATCH = { size: 24, gap: 7 } as const;

/** Rayon : capsule par défaut ; `ctrl` pour les pastilles denses en cellule de tableau. */
export const BADGE_RADIUS = { pill: 'pill', ctrl: 'ctrl' } as const;
export type BadgeRadius = keyof typeof BADGE_RADIUS;

/** Retrait (capsule de filtre appliqué) : le padding devient ASYMÉTRIQUE, la croix comble à droite. */
export const BADGE_REMOVE = { padLeft: 10, padRight: 6, hit: 16, icon: 9 } as const;
